import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiPhone, FiMapPin } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

// URL of your Google Sheets Web App endpoint (Google Apps Script).
// It checks process.env first (for secure Vercel deployments), falling back to the hardcoded string if empty.
const GOOGLE_SHEETS_WEBAPP_URL = "https://script.google.com/macros/s/AKfycbze68JjFpD2xeZianR4-L8sD3DbFux4m7_Vp51rSAJAhGnpWL_hY2nQmVXDkAH2SEOm8A/exec";

const ContactUs = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    message: "",
    smsConsent: false,
    privacyConsent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitModal, setSubmitModal] = useState({
    show: false,
    type: "success", // "success" | "error" | "validation"
    title: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleScrollToForm = (e) => {
    e.preventDefault();
    const element = document.getElementById("contact-form-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if the required fields are filled
    if (!formData.firstName || !formData.phone) {
      setSubmitModal({
        show: true,
        type: "validation",
        title: "Required Fields Missing",
        message: "Please fill in all required fields (First Name and Phone Number) to send your message.",
      });
      return;
    }

    if (!formData.privacyConsent) {
      setSubmitModal({
        show: true,
        type: "validation",
        title: "Consent Required",
        message: "Please accept the Privacy Policy and Terms & Conditions to proceed.",
      });
      return;
    }

    setIsSubmitting(true);

    if (!GOOGLE_SHEETS_WEBAPP_URL) {
      console.warn("GOOGLE_SHEETS_WEBAPP_URL is not set. Submitting in dev mode.");
      setIsSubmitting(false);
      setSubmitModal({
        show: true,
        type: "success",
        title: "Message Sent Successfully!",
        message: "Thank you for reaching out to Dr. Krishna's Dental! Our team has received your message and will get back to you shortly.",
      });
      setFormData({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        message: "",
        smsConsent: false,
        privacyConsent: false,
      });
      return;
    }

    const payload = new FormData();
    payload.append("firstName", formData.firstName);
    payload.append("lastName", formData.lastName);
    payload.append("phone", formData.phone);
    payload.append("email", formData.email);
    payload.append("message", formData.message);
    payload.append("smsConsent", formData.smsConsent ? "Yes" : "No");
    payload.append("submittedAt", new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }));

    fetch(GOOGLE_SHEETS_WEBAPP_URL, {
      method: "POST",
      body: payload,
      mode: "no-cors",
    })
      .then(() => {
        console.log("Data successfully saved to Google Sheets");
        setIsSubmitting(false);
        setSubmitModal({
          show: true,
          type: "success",
          title: "Message Sent Successfully!",
          message: "Thank you for reaching out to Dr. Krishna's Dental! Our team has received your message and will get back to you shortly.",
        });
        setFormData({
          firstName: "",
          lastName: "",
          phone: "",
          email: "",
          message: "",
          smsConsent: false,
          privacyConsent: false,
        }); // Clear fields after submission
      })
      .catch((error) => {
        console.error("Error saving data to Google Sheets:", error);
        setIsSubmitting(false);
        setSubmitModal({
          show: true,
          type: "error",
          title: "Submission Error",
          message: "There was an error sending your message. Please try again or call us directly at +91 6281785274.",
        });
      });
  };

  return (
    <div className="bg-[#F5F3E9] min-h-screen">
      {/* Hero Section */}
      <section
        className="relative pt-36 pb-20 md:pt-48 md:pb-28 flex items-center justify-center bg-cover text-white bg-[position:85%_35%] lg:bg-[position:80%_30%]"
        style={{
          backgroundImage: `url('/dental_hero_smile.jpg')`,
        }}
      >
        {/* Gradient Overlay: Darker on the left for text readability, clear on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20 md:to-transparent"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full text-center md:text-left">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-center md:justify-start space-x-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white transition-colors">
              {t.contactPage.breadcrumbHome}
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-white font-medium">{t.contactPage.breadcrumbContact}</span>
          </div>

          {/* Tagline */}
          <div className="flex items-center justify-center md:justify-start space-x-2 mb-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#E09F3E]">
              {t.contactPage.tagline}
            </p>
            <div className="w-8 h-[1px] bg-[#E09F3E]"></div>
          </div>

          {/* Main Title */}
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t.contactPage.title}
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl leading-relaxed mb-8">
            {t.contactPage.subtitle}
          </p>

          {/* Phone Button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
            <a
              href="tel:6281785274"
              className="bg-white/10 border border-white/20 text-white hover:bg-white hover:text-black font-semibold px-6 py-3 rounded-full transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <span>📞 +91 6281785274</span>
            </a>
            <a
              href="tel:8186871685"
              className="bg-white/10 border border-white/20 text-white hover:bg-white hover:text-black font-semibold px-6 py-3 rounded-full transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <span>📞 +91 8186871685</span>
            </a>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="contact-form-section" className="py-16 md:py-24">
        {/* Upper Line Header */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between mb-12">
          <span className="text-sm font-light text-gray-500 tracking-widest">
            [01]
          </span>
          <div className="flex-grow border-t border-gray-300 mx-6"></div>
          <span className="text-xs font-semibold text-gray-600 tracking-widest uppercase">
            Send a Message
          </span>
        </div>

        {/* Main Grid */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Info */}
          <div className="lg:col-span-5 text-[#1E293B]">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              How Can We Help?
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-12">
              Fill out the form below and our team will get back to you shortly.
              Whether you have a question or are ready to schedule, we look forward
              to hearing from you.
            </p>

            <div className="space-y-8">
              {/* Phone Info */}
              <div className="flex items-start space-x-4">
                <div className="bg-gray-100 p-3 rounded-full text-gray-700 mt-1">
                  <FiPhone className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">
                    Phone
                  </h4>
                  <div className="text-lg font-medium space-y-1">
                    <a
                      href="tel:6281785274"
                      className="block hover:text-blue-600 transition-colors"
                    >
                      +91 6281785274
                    </a>
                    <a
                      href="tel:8186871685"
                      className="block hover:text-blue-600 transition-colors"
                    >
                      +91 8186871685
                    </a>
                  </div>
                </div>
              </div>

              {/* Address Info */}
              <div className="flex items-start space-x-4">
                <div className="bg-gray-100 p-3 rounded-full text-gray-700 mt-1">
                  <FiMapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">
                    Address
                  </h4>
                  <p className="text-lg font-medium leading-relaxed max-w-sm">
                    First Floor, 6-119/3/2, Dammaiguda Rd, Dattatreya Nagar Colony,
                    Nagaram, Hyderabad, Secunderabad, Telangana 500083
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100 space-y-6"
            >
              {/* Hidden Input to map combined full name for EmailJS template */}
              <input
                type="hidden"
                name="name"
                value={`${formData.firstName} ${formData.lastName}`.trim()}
              />

              {/* Name Fields Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    className="w-full p-4 bg-[#F5F3E9] border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition duration-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    className="w-full p-4 bg-[#F5F3E9] border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition duration-200"
                  />
                </div>
              </div>

              {/* Contact Fields Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="w-full p-4 bg-[#F5F3E9] border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(918) 000-0000"
                    className="w-full p-4 bg-[#F5F3E9] border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition duration-200"
                    required
                  />
                </div>
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  className="w-full p-4 bg-[#F5F3E9] border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none h-32 resize-none transition duration-200"
                  required
                ></textarea>
              </div>

              {/* Consent Checkboxes */}
              <div className="space-y-4 pt-2">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="smsConsent"
                    checked={formData.smsConsent}
                    onChange={handleChange}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="text-xs text-gray-500 leading-normal">
                    By checking this box, you agree to receive SMS care-related updates
                    from Dr. Krishna's Dental at the phone number provided. Message
                    frequency may vary. Message and data rates may apply. Reply
                    STOP to opt out or HELP for help. Consent is not a condition of
                    purchase.
                  </span>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacyConsent"
                    checked={formData.privacyConsent}
                    onChange={handleChange}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    required
                  />
                  <span className="text-xs text-gray-500 leading-normal">
                    I have read and agree to the{" "}
                    <a
                      href="#privacy"
                      className="underline hover:text-teal-600 transition-colors"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="#terms"
                      className="underline hover:text-teal-600 transition-colors"
                    >
                      Terms & Conditions
                    </a>
                    . <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#0F4C5C] text-white font-medium px-8 py-4 rounded-full shadow-md hover:bg-[#0c3c49] transition-all duration-300 outline-none disabled:opacity-60 flex items-center justify-center space-x-2 min-w-[170px]"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>Send Message</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Visit Us Section (2nd Section) */}
      <section className="py-16 md:py-24 bg-[#0F4C5C] text-white animate-fade-in">
        {/* Upper Line Header */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between mb-12">
          <span className="text-sm font-light text-teal-300/60 tracking-widest">
            [02]
          </span>
          <div className="flex-grow border-t border-teal-500/30 mx-6"></div>
          <span className="text-xs font-semibold text-teal-200 tracking-widest uppercase">
            Visit Us
          </span>
        </div>

        {/* Main Grid */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Location Details */}
          <div className="lg:col-span-7 space-y-8">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white">
              Visit Our Dammaiguda Office
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Address */}
              <div className="border border-white/20 rounded-2xl p-6 bg-white/5">
                <h4 className="text-xs font-bold text-teal-300 tracking-wider uppercase mb-3">
                  Address
                </h4>
                <a
                  href="https://www.google.com/maps/place/Dr.+Krishna's+Dental+Braces+%26+Implant+center/@17.4905101,78.5994577,17z/data=!3m1!5s0x3bcb9cf07ad9b185:0x83e5f61074131207!4m8!3m7!1s0x3bcb9d64f9c20e93:0x7df2ea8ca4e570f5!8m2!3d17.4905101!4d78.5994577!9m1!1b1!16s%2Fg%2F11wtvyrmxy?hl=en&entry=ttu"
                  target="_blank"
                  rel="noreferrer"
                  className="text-lg font-medium leading-relaxed hover:text-[#E09F3E] transition-colors text-white"
                >
                  First Floor, 6-119/3/2, Dammaiguda Rd, Dattatreya Nagar Colony,
                  Nagaram, Hyderabad, Telangana 500083
                </a>
              </div>

              {/* Phone */}
              <div className="border border-white/20 rounded-2xl p-6 bg-white/5">
                <h4 className="text-xs font-bold text-teal-300 tracking-wider uppercase mb-3">
                  Phone
                </h4>
                <div className="text-lg font-medium space-y-1">
                  <a
                    href="tel:6281785274"
                    className="block hover:text-[#E09F3E] transition-colors text-white"
                  >
                    +91 6281785274
                  </a>
                  <a
                    href="tel:8186871685"
                    className="block hover:text-[#E09F3E] transition-colors text-white"
                  >
                    +91 8186871685
                  </a>
                </div>
              </div>

              {/* Office Hours */}
              <div className="border border-white/20 rounded-2xl p-6 bg-white/5">
                <h4 className="text-xs font-bold text-teal-300 tracking-wider uppercase mb-3">
                  Office Hours
                </h4>
                <div className="space-y-2 text-sm text-teal-100">
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => (
                    <div key={day} className="flex justify-between border-b border-white/5 pb-1.5">
                      <span className="text-white font-medium">{day}</span>
                      <span className="text-right ml-4">
                        10:00 AM – 9:00 PM
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Areas */}
              <div className="border border-white/20 rounded-2xl p-6 bg-white/5">
                <h4 className="text-xs font-bold text-teal-300 tracking-wider uppercase mb-3">
                  Service Areas
                </h4>
                <ul className="text-lg font-medium text-teal-100 space-y-2">
                  {["Nagaram", "Dammaiguda", "A.S. Rao Nagar", "Kushaiguda", "ECIL"].map((area) => (
                    <li key={area} className="flex items-center space-x-2">
                      <span className="text-[#E09F3E] text-2xl leading-none mr-1">•</span>
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Social media links or Maps review click */}
            <div className="flex space-x-4 pt-4">
              <a
                href="https://www.google.com/maps/place/Dr.+Krishna's+Dental+Braces+%26+Implant+center/@17.4905101,78.5994577,17z/data=!3m1!5s0x3bcb9cf07ad9b185:0x83e5f61074131207!4m8!3m7!1s0x3bcb9d64f9c20e93:0x7df2ea8ca4e570f5!8m2!3d17.4905101!4d78.5994577!9m1!1b1!16s%2Fg%2F11wtvyrmxy?hl=en&entry=ttu"
                target="_blank"
                rel="noreferrer"
                className="bg-white/10 border border-white/20 p-3 rounded-full hover:bg-white hover:text-[#0F4C5C] transition-all text-white"
                title="Google Maps"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right Column: Google Maps Embed */}
          <div className="lg:col-span-5 h-[680px] w-full bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.1091599818817!2d78.57149027581561!3d17.47839369970894!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9d64f9c20e93%3A0x7df2ea8ca4e570f5!2sKrishna&#39;s%20Dental!5e0!3m2!1sen!2sin!4v1717290117094!5m2!1sen!2sin"
              title="Dr. Krishna's Dental Location Map"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Call to Action Section (Section 3) */}
      <section className="py-20 md:py-28 bg-[#F5F3E9] text-center">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Tagline */}
          <div className="flex items-center justify-center space-x-2 mb-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#E09F3E]">
              Next Steps
            </p>
            <div className="w-8 h-[1px] bg-[#E09F3E]"></div>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0F4C5C] mb-6">
            Start Your Journey to a Healthier Smile
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10 max-w-2xl mx-auto">
            Whether you’re scheduling your first visit, returning for care, or
            just have a quick question, our team is ready to help you take the next step.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleScrollToForm}
              className="bg-[#0F4C5C] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:bg-[#0c3c49] transition-all duration-300 w-full sm:w-auto"
            >
              Book Appointment
            </button>
            <a
              href="tel:6281785274"
              className="border border-[#0F4C5C] text-[#0F4C5C] font-semibold px-8 py-4 rounded-full hover:bg-[#0F4C5C] hover:text-white transition-all duration-300 w-full sm:w-auto text-center"
            >
              Call +91 6281785274
            </a>
          </div>
        </div>
      </section>

      {/* Custom Submit Notification Modal */}
      {submitModal.show && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-opacity animate-fade-in">
          <div className="bg-white rounded-3xl p-8 md:p-10 max-w-md w-full text-center shadow-2xl border border-gray-100 transform transition-all animate-scale-up">
            
            {/* Icon based on type */}
            {submitModal.type === "success" && (
              <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}

            {submitModal.type === "validation" && (
              <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            )}

            {submitModal.type === "error" && (
              <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}

            {/* Title */}
            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-3">
              {submitModal.title}
            </h3>

            {/* Message */}
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-8">
              {submitModal.message}
            </p>

            {/* Action Button */}
            <button
              type="button"
              onClick={() => setSubmitModal({ ...submitModal, show: false })}
              className="w-full py-3.5 bg-[#0F4C5C] hover:bg-[#0c3c49] text-white font-bold rounded-full transition-all duration-300 shadow-md text-sm tracking-wide"
            >
              {submitModal.type === "success" ? "Great, Thanks!" : "Got it"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUs;
